import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, adminHeaders, formatGBP, formatDate } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['pending', 'in_progress', 'completed', 'shipped'];

export default function AdminOrderDetail() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState('');
    const [viewing, setViewing] = useState(null);
    const [saving, setSaving] = useState(false);

    async function load() {
        const r = await api.get(`/admin/orders/${id}`, { headers: adminHeaders() });
        setOrder(r.data);
        setStatus(r.data.status);
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function saveStatus() {
        setSaving(true);
        try {
            await api.patch(`/admin/orders/${id}/status`, { status }, { headers: adminHeaders() });
            toast.success('Order status updated.');
            load();
        } catch (e) {
            toast.error('Failed to update status.');
        } finally {
            setSaving(false);
        }
    }

    if (!order) return <div className="text-[color:var(--muted-fg)]" data-testid="loading-state">Loading…</div>;

    const p = order.pricing || {};

    return (
        <div>
            <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm text-[color:var(--graphite)] under-link mb-6"><ArrowLeft className="h-4 w-4" /> Back to orders</Link>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                    <p className="caption">Order {order.id.slice(0, 8).toUpperCase()}</p>
                    <h1 className="font-display text-3xl text-[color:var(--umber)] mt-1">{order.customer?.name || 'Unnamed customer'}</h1>
                    <p className="text-sm text-[color:var(--muted-fg)] mt-1">{formatDate(order.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                    <Badge className={order.payment_status === 'paid' ? 'bg-[rgba(47,125,90,0.18)] text-[color:var(--umber)] border border-[rgba(47,125,90,0.35)]' : 'bg-[color:var(--paper)] text-[color:var(--graphite)] border border-[color:var(--border)]'}>Payment: {order.payment_status}</Badge>
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger data-testid="admin-order-status-select" className="w-48 bg-[color:var(--paper-2)] border-[color:var(--border)]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button onClick={saveStatus} disabled={saving} data-testid="admin-order-status-save-button" className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)]">
                        {saving ? 'Saving…' : 'Update'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] p-6 space-y-5">
                    <div>
                        <p className="caption mb-2">Selections</p>
                        <div className="text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-[color:var(--graphite)]">Size</span><span>{p.size?.label}</span></div>
                            <div className="flex justify-between"><span className="text-[color:var(--graphite)]">Framing</span><span>{p.frame?.label}</span></div>
                            <div className="flex justify-between"><span className="text-[color:var(--graphite)]">Delivery</span><span>{p.delivery?.label}</span></div>
                            {p.print_addons && p.print_addons.length > 0 && (
                                <div className="pt-2 border-t border-dashed border-[color:var(--border)] mt-2">
                                    <p className="caption mb-1">Print add-ons</p>
                                    {p.print_addons.map((a) => (
                                        <div key={a.size_id} className="flex justify-between">
                                            <span className="text-[color:var(--graphite)]">{a.size?.label} × {a.quantity}</span>
                                            <span>{formatGBP(a.line_total)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="flex justify-between pt-2 border-t border-[color:var(--border)] mt-2"><span>Total</span><span className="font-medium">{formatGBP(order.total)}</span></div>
                        </div>
                    </div>
                    <div>
                        <p className="caption mb-2">Customer</p>
                        {order.customer ? (
                            <div className="text-sm text-[color:var(--graphite)] leading-relaxed">
                                <div>{order.customer.name}</div>
                                <div>{order.customer.email}</div>
                                {order.customer.phone && <div>{order.customer.phone}</div>}
                                <div className="mt-2">{order.customer.address_line1}{order.customer.address_line2 ? `, ${order.customer.address_line2}` : ''}</div>
                                <div>{order.customer.city}, {order.customer.postcode}</div>
                                <div>{order.customer.country}</div>
                            </div>
                        ) : <p className="text-sm text-[color:var(--muted-fg)]">Not provided</p>}
                    </div>
                    <div>
                        <p className="caption mb-2">Customer notes</p>
                        <p className="text-sm text-[color:var(--graphite)] whitespace-pre-line">{order.notes || <span className="text-[color:var(--muted-fg)]">None.</span>}</p>
                    </div>
                </div>
                <div className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--paper-2)] p-6">
                    <p className="caption mb-2">Reference photos ({order.images?.length || 0})</p>
                    {(!order.images || order.images.length === 0) ? (
                        <p className="text-sm text-[color:var(--muted-fg)]">No reference photos.</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {order.images.map((img) => (
                                <button key={img.id} onClick={() => setViewing(img)} data-testid="admin-order-reference-image" className="aspect-square rounded-xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--paper)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]">
                                    <img src={img.data_url} alt={img.filename} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
                <DialogContent className="max-w-4xl bg-[color:var(--paper)] border-[color:var(--border)]">
                    <DialogTitle className="sr-only">{viewing?.filename}</DialogTitle>
                    {viewing && (
                        <div>
                            <img src={viewing.data_url} alt={viewing.filename} className="w-full h-auto rounded-xl" />
                            <p className="text-xs text-[color:var(--muted-fg)] mt-3">{viewing.filename} · {(viewing.size / 1024).toFixed(0)} KB</p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
